import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-24">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-25">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-26">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-27">
              <div className="flex items-center gap-3" data-id="element-28">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-29">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-30" />
                </div>
                <div data-id="element-31">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-32">
                    Snakker · Customer Overview
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-33">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-34">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-35" />
              </button>
            </div>

            {/* Written summary */}
            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-36">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-37">
                Snakker is the AI-native engine for{' '}
                <span className="text-[#6C2BD9]" data-id="element-38">
                  Legacy Brand Optimization
                </span>{' '}
                — unlocking significant value from pharma's revenue tail through
                continuous, scalable optimization of mature and off-patent
                brands.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-39">
                Strategic Focus
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-40">
                Every pharma product moves through the same four phases.{' '}
                <strong data-id="element-41">R&D</strong> is the long period of discovery and
                clinical development where investment runs deep into the
                negative. <strong data-id="element-42">Launch</strong> is the rapid rise as the brand
                reaches the market, gains approval and begins generating
                revenue. <strong data-id="element-43">Mature</strong> is the peak — the brand
                commands strong share, prescribers are engaged, and the product
                funds the rest of the portfolio. Then comes{' '}
                <strong data-id="element-44">Legacy</strong>: patents expire, attention shifts to
                newer assets, marketing investment is pulled, and revenue
                typically falls off a cliff and flattens into a thin tail just
                above zero. Snakker focuses entirely on this Legacy phase — the
                strategic opportunity is to <em data-id="element-45">soften the cliff</em> and extend
                the tail, where even small improvements compound into enormous
                portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-46">
                The Market
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-47">
                Roughly <strong data-id="element-48">30–35% of big pharma revenue</strong> comes from
                mature and legacy brands, and{' '}
                <strong data-id="element-49">
                  $180–300B+ in revenue is facing loss of exclusivity
                </strong>{' '}
                in the coming years. This is the part of the portfolio that
                quietly funds the next generation of innovation — and it is the
                most under-optimized segment in the industry.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-50">
                The Problem
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-51">
                When patents expire, generic competition can cause sales to drop
                more than 90% almost overnight for small molecules — and
                marketing investment is stopped immediately. Sales teams are
                reassigned, budgets are cut, and when the gap between revenue
                and cost approaches zero the product is taken off the market.
                The real problem isn't that these brands lack value — it's that{' '}
                <strong data-id="element-52">traditional marketing is simply too expensive</strong>{' '}
                to justify on declining revenue, so brands die earlier than they
                need to. Biologics face the same end state, just on a slower
                curve.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-53">
                The Unlocked Value
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-54">
                Even a <strong data-id="element-55">1% improvement</strong> across a legacy portfolio
                represents tens of millions of euros per year. Applied to a
                pharma division like Roche (~€45B revenue, ~35% legacy), this is
                currently unmanaged, under-optimized value sitting on the table
                — and it compounds every quarter it's left alone.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-56">
                Why AI Alone Isn't Enough
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-57">
                Generative AI dramatically reduces the cost of content creation,
                making it theoretically possible to support the long tail. But
                AI alone is not enough.{' '}
                <strong data-id="element-58">Execution is the bottleneck</strong> — what actually
                moves legacy revenue is strategy, compliance, distribution and
                measurement working together as a system.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-59">
                From Bottleneck to Breakthrough
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-60">
                Backed by an <strong data-id="element-61">Innobooster grant</strong>, we have turned
                thirty years of pharma campaign models into AI workflows.
                Storyboarding and creation now happen in minutes, not months,
                producing short, engaging, TA-relevant campaigns ready for
                deployment. Four things make Snakker structurally different:{' '}
                <strong data-id="element-62">deep vertical expertise and master agreements</strong>{' '}
                in regulated environments where generalist AI fails; a{' '}
                <strong data-id="element-63">one-to-one</strong> precision delivery model versus
                agencies' broad-stroke campaigns; a{' '}
                <strong data-id="element-64">proprietary interactive engagement platform</strong>{' '}
                that drives measurable outcomes; and a{' '}
                <strong data-id="element-65">compounding flywheel</strong> in which behavioral data
                refines the model weekly, widening the gap over time.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-66">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-67">
                Snakker is not an agency and not a SaaS tool. We are creating a
                new category — <strong data-id="element-68">Campaign Service Management</strong> —
                managed pharma campaign execution powered by software economics.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-69">
                Three Levers
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-70">
                Our approach pulls three levers that compound on each other. We{' '}
                <strong data-id="element-71">reduce erosion</strong> through continuous, data-driven
                engagement that keeps HCPs connected to the brand, actively
                defending market share instead of letting it fade. We{' '}
                <strong data-id="element-72">increase profitability</strong> by replacing traditional
                marketing with AI-automated execution, widening the margin on
                every euro of legacy revenue. And we{' '}
                <strong data-id="element-73">prolong lifespan</strong> — lower costs and slower
                erosion keep the gap between revenue and cost positive for
                longer, so products stay on market generating value instead of
                being withdrawn prematurely.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-74">
                The Operating Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-75">
                Snakker operates on four pillars. We{' '}
                <strong data-id="element-76">leverage existing assets</strong> — no new content
                creation is required, we repurpose approved materials into
                interactive formats. We achieve{' '}
                <strong data-id="element-77">rapid deployment</strong> through centralized creation
                with localized distribution, launching in weeks rather than
                months. We perform <strong data-id="element-78">behavioral data capture</strong> that
                moves beyond vanity metrics to reveal what actually drives HCP
                decisions and knowledge gaps. And we run a{' '}
                <strong data-id="element-79">continuous optimization</strong> loop where every
                interaction informs and improves the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-80">
                What Snakker Does
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-81">
                Snakker acts as the operating layer between strategy and
                execution. We create campaigns from existing approved content,
                turn them into short interactive <strong data-id="element-82">Snaks™</strong>,
                publish them across channels, capture behavioral data on every
                interaction, and improve campaign after campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-83">
                The Platform & Flywheel
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-84">
                The platform is five connected components — a{' '}
                <strong data-id="element-85">Creation Engine</strong>, an{' '}
                <strong data-id="element-86">Interactive Player</strong>, a{' '}
                <strong data-id="element-87">Publishing</strong> layer, a{' '}
                <strong data-id="element-88">Streaming Runtime</strong>, and{' '}
                <strong data-id="element-89">Data & Optimization</strong>. Behind them runs an
                AI-driven closed-loop architecture organized as Think · Create ·
                Learn · Improve, built on three layers: the{' '}
                <strong data-id="element-90">AI Intelligence Engine</strong> (blueprint analysis,
                story generation, content creation, channel optimization), the{' '}
                <strong data-id="element-91">Data Supply Chain</strong> (therapeutic-area themes,
                brand data, HCP insights, market intelligence), and the{' '}
                <strong data-id="element-92">Application Suite</strong> (Snak Composer, Snakker
                Player, Distribution, Analytics). The result is a flywheel that
                runs Launch → Engage → Measure → Learn → Optimize → Relaunch —
                every campaign making the next one smarter.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-93">
                The Moat
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-94">
                The defensibility comes from four compounding assets:{' '}
                <strong data-id="element-95">medical storytelling</strong> as codified pharma
                expertise, a{' '}
                <strong data-id="element-96">short-format interactive engagement platform</strong>{' '}
                driving measurable outcomes, deep{' '}
                <strong data-id="element-97">pharma distribution</strong> relationships, and a
                privacy-safe <strong data-id="element-98">behavioural dataset</strong> that compounds
                performance with every campaign. Together they create an
                Unbreakable Flywheel that no broad player can match inside this
                regulated vertical.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-99">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-100">
                Snakker is Campaign Service Management for legacy pharma —
                turning the 30–35% of revenue everyone has given up on into the
                most profitable and defensible part of the portfolio.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}